import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import type { paths } from "@/types/api";

type CreateBody =
  paths["/api/v1/invoices"]["post"]["requestBody"]["content"]["application/json"];

export const invoiceKeys = {
  all: ["invoices"] as const,
  list: (status: string) => ["invoices", "list", status] as const,
  detail: (id: string) => ["invoices", "detail", id] as const,
};

export function useInvoices(status?: string) {
  return useQuery({
    queryKey: invoiceKeys.list(status ?? "all"),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/invoices", {
        params: { query: status ? { status_filter: status } : {} },
      });
      if (error || !data) throw new Error("Failed to load invoices");
      return data;
    },
  });
}

export function useContractInvoices(contractId: string | undefined) {
  return useQuery({
    queryKey: ["invoices", "contract", contractId ?? ""],
    enabled: Boolean(contractId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/invoices", {
        params: { query: { contract_id: contractId! } },
      });
      if (error || !data) throw new Error("Failed to load invoices");
      return data;
    },
  });
}

export function useInvoice(id: string | undefined) {
  return useQuery({
    queryKey: invoiceKeys.detail(id ?? ""),
    enabled: Boolean(id),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/invoices/{invoice_id}", {
        params: { path: { invoice_id: id! } },
      });
      if (error || !data) throw new Error("Failed to load invoice");
      return data;
    },
  });
}

export function useCreateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateBody) => {
      const { data, error } = await apiClient.POST("/api/v1/invoices", { body });
      if (error || !data) throw new Error("Failed to create invoice");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: invoiceKeys.all }),
  });
}

type PaymentBody =
  paths["/api/v1/invoices/{invoice_id}/payments"]["post"]["requestBody"]["content"]["application/json"];

export function useRecordPayment(invoiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: PaymentBody) => {
      const { data, error } = await apiClient.POST(
        "/api/v1/invoices/{invoice_id}/payments",
        { params: { path: { invoice_id: invoiceId } }, body },
      );
      if (error || !data) throw new Error("Failed to record payment");
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: invoiceKeys.detail(invoiceId) });
      qc.invalidateQueries({ queryKey: invoiceKeys.all });
      qc.invalidateQueries({ queryKey: ["ledger"] });
    },
  });
}

export function useIssueInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await apiClient.POST(
        "/api/v1/invoices/{invoice_id}/issue",
        { params: { path: { invoice_id: id } } },
      );
      if (error || !data) throw new Error("Failed to issue invoice");
      return data;
    },
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: invoiceKeys.all });
      qc.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
      qc.invalidateQueries({ queryKey: ["ledger"] });
    },
  });
}

export function useCreditInvoice(invoiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (reason: string | null) => {
      const { data, error } = await apiClient.POST(
        "/api/v1/invoices/{invoice_id}/credit",
        { params: { path: { invoice_id: invoiceId } }, body: { reason } },
      );
      if (error || !data) throw new Error("Failed to credit invoice");
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: invoiceKeys.detail(invoiceId) });
      qc.invalidateQueries({ queryKey: invoiceKeys.all });
      qc.invalidateQueries({ queryKey: ["ledger"] });
    },
  });
}
